import TimeAgo from 'timeago-react';
import * as timeago from 'timeago.js';

import { useMemo, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

interface RelativeTimeProps {
  /** ISO date string or Date object */
  date: string | Date;
  /** Whether to auto-update the time (default: true) */
  live?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// Time constants
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

// Track registered locales to avoid re-registration
const registeredLocales = new Set<string>();

/**
 * Create a timeago locale function using i18n translations.
 * Format: [past, future] for each time unit index.
 * Indices: 0=just now, 1=seconds, 2=1 minute, 3=minutes, 4=1 hour, 5=hours,
 *          6=1 day, 7=days, 8=1 week, 9=weeks, 10=1 month, 11=months, 12=1 year, 13=years
 */
function createLocale(
  t: (key: string, options?: Record<string, unknown>) => string
): timeago.LocaleFunc {
  return (number: number, index: number): [string, string] => {
    // Each index maps to a specific time unit
    // First element = past tense (displayed), Second element = future tense (unused)
    const pairs: [string, string][] = [
      // 0: just now (very recent)
      [t('common:timeAgo.justNow'), t('common:timeAgo.justNow')],
      // 1: X seconds - show "just now" instead (user preference)
      [t('common:timeAgo.justNow'), t('common:timeAgo.justNow')],
      // 2: 1 minute
      [
        t('common:timeAgo.minutesAgo', { count: 1 }),
        t('common:timeAgo.minutesAgo', { count: 1 }),
      ],
      // 3: X minutes
      [
        t('common:timeAgo.minutesAgo', { count: number }),
        t('common:timeAgo.minutesAgo', { count: number }),
      ],
      // 4: 1 hour
      [
        t('common:timeAgo.hoursAgo', { count: 1 }),
        t('common:timeAgo.hoursAgo', { count: 1 }),
      ],
      // 5: X hours
      [
        t('common:timeAgo.hoursAgo', { count: number }),
        t('common:timeAgo.hoursAgo', { count: number }),
      ],
      // 6: 1 day (yesterday)
      [t('common:timeAgo.yesterday'), t('common:timeAgo.yesterday')],
      // 7: X days
      [
        t('common:timeAgo.daysAgo', { count: number }),
        t('common:timeAgo.daysAgo', { count: number }),
      ],
      // 8: 1 week
      [
        t('common:timeAgo.weeksAgo', { count: 1 }),
        t('common:timeAgo.weeksAgo', { count: 1 }),
      ],
      // 9: X weeks
      [
        t('common:timeAgo.weeksAgo', { count: number }),
        t('common:timeAgo.weeksAgo', { count: number }),
      ],
      // 10: 1 month
      [
        t('common:timeAgo.monthsAgo', { count: 1 }),
        t('common:timeAgo.monthsAgo', { count: 1 }),
      ],
      // 11: X months
      [
        t('common:timeAgo.monthsAgo', { count: number }),
        t('common:timeAgo.monthsAgo', { count: number }),
      ],
      // 12: 1 year - convert to 12 months (dates >2 days show formatted anyway)
      [
        t('common:timeAgo.monthsAgo', { count: 12 }),
        t('common:timeAgo.monthsAgo', { count: 12 }),
      ],
      // 13: X years - convert to months
      [
        t('common:timeAgo.monthsAgo', { count: number * 12 }),
        t('common:timeAgo.monthsAgo', { count: number * 12 }),
      ],
    ];
    return pairs[index];
  };
}

/**
 * Displays a relative time that auto-updates efficiently using shared timers.
 * - < 1 minute: "just now"
 * - < 1 hour: "X minutes ago"
 * - < 24 hours: "X hours ago"
 * - < 48 hours: "yesterday"
 * - older: formatted date (e.g., "Dec 15" or "Dec 15, 2023")
 *
 * Uses timeago-react for efficient updates (shared timer across all instances).
 * Translations come from i18n locale files (common:timeAgo.*).
 */
export function RelativeTime({
  date,
  live = true,
  className,
}: RelativeTimeProps) {
  const { t, i18n } = useTranslation();

  // Get the base language code
  const baseLocale = useMemo(() => {
    const lang = i18n?.language?.split('-')[0] || 'en';
    return ['en', 'es', 'fr', 'pt', 'de'].includes(lang) ? lang : 'en';
  }, [i18n?.language]);

  // Create a unique locale name for this language + i18n combo
  // Register locale synchronously to avoid race condition with TimeAgo render
  const localeName = useMemo(() => {
    const name = `${baseLocale}-custom`;
    if (!registeredLocales.has(name)) {
      timeago.register(name, createLocale(t));
      registeredLocales.add(name);
    }
    return name;
  }, [baseLocale, t]);

  // Parse the date
  // Handle date-only strings (YYYY-MM-DD) and ISO midnight dates as local time
  const dateObj = useMemo(() => {
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string') {
      // Date-only strings should be parsed as local time, not UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      // ISO date strings at midnight UTC should also be treated as local dates
      const isoMidnightMatch = date.match(
        /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/
      );
      if (isoMidnightMatch) {
        const [, year, month, day] = isoMidnightMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
    }
    return new Date(date);
  }, [date]);

  // Check if date is older than 2 days (show formatted date instead)
  // Using useState initializer to capture current time once on mount
  const [mountTime] = useState(() => Date.now());
  const isOlderThanTwoDays = useMemo(() => {
    const diff = mountTime - dateObj.getTime();
    return diff > TWO_DAYS_MS;
  }, [dateObj, mountTime]);

  // For dates older than 2 days, show formatted date
  const currentYear = useMemo(
    () => new Date(mountTime).getFullYear(),
    [mountTime]
  );
  if (isOlderThanTwoDays) {
    const formattedDate = dateObj.toLocaleDateString(baseLocale, {
      month: 'short',
      day: 'numeric',
      year: dateObj.getFullYear() !== currentYear ? 'numeric' : undefined,
    });
    return <span className={className}>{formattedDate}</span>;
  }

  // For recent dates, use TimeAgo with shared timer
  return (
    <TimeAgo
      datetime={dateObj}
      locale={localeName}
      live={live}
      className={className}
    />
  );
}
